/**
 * UserMenu — the account control at the right end of the topbar.
 *
 * The trigger is a plain button so Dropdown can clone the menu-button ARIA onto it;
 * it carries an explicit `aria-label` because the visible name and role badge are
 * progressively hidden on narrow screens and an avatar alone names nothing.
 *
 * Signing out clears the session first and only then navigates, so the guard on
 * `/login` never sees a half-torn-down session and bounces the user back.
 */
import { forwardRef, useCallback, type HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Settings, User } from 'lucide-react';
import { ROLE_DISPLAY } from '@aforium/shared';
import { useAuth } from '../../providers/AuthProvider';
import { Avatar } from '../ui/Avatar';
import { EnumBadge } from '../ui/Badge';
import { Dropdown, type DropdownItem } from '../ui/Dropdown';
import { cn } from '../../lib/cn';

export type UserMenuProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'>;

export const UserMenu = forwardRef<HTMLDivElement, UserMenuProps>(function UserMenu(
  { className, ...props },
  ref,
) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = useCallback(() => {
    void (async () => {
      await logout();
      navigate('/login', { replace: true });
    })();
  }, [logout, navigate]);

  // Rendered inside the authenticated shell, but the topbar must not crash on the
  // frame between a sign-out clearing the session and the redirect landing.
  if (!user) return null;

  const role = ROLE_DISPLAY[user.role];

  const items: DropdownItem[] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <User className="h-4 w-4" aria-hidden="true" />,
      onSelect: () => navigate('/profile'),
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <Settings className="h-4 w-4" aria-hidden="true" />,
      onSelect: () => navigate('/settings'),
    },
    {
      key: 'sign-out',
      label: 'Sign out',
      icon: <LogOut className="h-4 w-4" aria-hidden="true" />,
      tone: 'danger',
      separatorBefore: true,
      onSelect: handleSignOut,
    },
  ];

  return (
    <Dropdown
      ref={ref}
      align="end"
      label="Account"
      items={items}
      className={cn('shrink-0', className)}
      trigger={
        <button
          type="button"
          aria-label={`Account menu for ${user.name}`}
          className="flex h-9 max-w-[14rem] items-center gap-2 rounded-lg pl-1 pr-2 text-left transition-colors hover:bg-surface-sunken"
        >
          <Avatar name={user.name} src={user.avatar ?? undefined} size="sm" />

          <span className="hidden min-w-0 flex-col leading-tight md:flex">
            <span className="truncate text-sm font-medium text-content">{user.name}</span>
            <span className="truncate text-2xs text-content-subtle">{user.email}</span>
          </span>

          <EnumBadge display={role} size="sm" className="hidden lg:inline-flex" withTooltip={false} />

          <ChevronDown className="h-4 w-4 shrink-0 text-content-subtle" aria-hidden="true" />
        </button>
      }
      {...props}
    />
  );
});
